import { useCallback, useState } from 'react';
import type { Trace } from '@/features/traceability/type/Tool';
import type { TraceabilityApiClient } from '@/features/traceability/api/TraceabilityApiClient';
import { buildTraceFromValues } from '../utils/buildTraceFromValues';
import { JobRequestHandler } from '@/lib/http/JobRequestHandler';
import type { TraceResponse } from '@/features/traceability/api/TraceabilityResponseType';
import { useTranslation } from '@/hooks/useTranslation';

/**
 * Convert total seconds to "HH:MM:SS" string for DurationInput.
 */
function secondsToHMS(seconds: number | null): string {
  if (seconds === null || seconds === undefined) return '';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return [h, m, s].map((v) => String(v).padStart(2, '0')).join(':');
}

/**
 * Build flat editValues from a Trace (inverse of buildTraceFromValues).
 */
function traceToEditValues(trace: Trace | null): Record<string, unknown> {
  if (!trace) return {};
  return {
    toolId: trace.toolId ?? '',
    operator: trace.operator ?? '',
    operation: trace.operation ?? '',
    duration_total: secondsToHMS(trace.totalMachiningDuration),
    duration_piece: secondsToHMS(trace.pieceMachiningDuration),
    pieces: trace.machinedPieces ?? '',
    volume: trace.machinedVolume ?? '',
    distance: trace.machinedDistance ?? '',
    malrond: trace.malround ?? '',
    ae: trace.ae ?? '',
    ap: trace.ap ?? '',
    vc: trace.vc ?? '',
    fz: trace.fz ?? '',
    machineId: trace.machine?.id ?? null,
    createdAt: trace.createdAt ?? null,
    machineName: trace.machine?.name ?? '',
    machineBrand: trace.machine?.brand ?? '',
    machineModel: trace.machine?.model ?? '',
    machineSerial: trace.machine?.serialNumber ?? '',
    machineCompanyName: trace.machine?.companyName ?? '',
    machine: trace.machine ?? null,
    material: trace.material ?? null,
    materialLot: trace.materialLot ?? null,
    lubType: trace.lubType ?? '',
    lubBrand: trace.lubBrand ?? '',
    clampingType: trace.clampingType ?? '',
    clampingBrand: trace.clampingBrand ?? '',
    holderType: trace.holderType ?? '',
    holderBrand: trace.holderBrand ?? '',
    toolState: trace.toolState?.toolState ?? '',
    toolChange: trace.toolState?.changeReason ?? [],
    commentary: trace.toolState?.note ?? '',
  };
}

export type UseTraceEditReturn = {
  editValues: Record<string, unknown>;
  isDirty: boolean;
  isSaving: boolean;
  saveError: string | null;
  setField: (name: string, value: unknown) => void;
  reset: () => void;
  save: () => Promise<boolean>;
};

export function useTraceEdit(
  traceabilityApiClient: TraceabilityApiClient | null,
  trace: Trace | null
): UseTraceEditReturn {
  const [editValues, setEditValues] = useState<Record<string, unknown>>(() =>
    traceToEditValues(trace)
  );
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const { t } = useTranslation();

  const setField = useCallback((name: string, value: unknown) => {
    setEditValues((prev) => ({ ...prev, [name]: value }));
    setIsDirty(true);
  }, []);

  const reset = useCallback(() => {
    setEditValues(traceToEditValues(trace));
    setIsDirty(false);
    setSaveError(null);
  }, [trace]);

  const save = useCallback(async (): Promise<boolean> => {
    if (!traceabilityApiClient || !trace) return false;

    const traceId = trace.traceId;
    if (traceId === undefined) return false;

    const builtTrace = buildTraceFromValues({ ...editValues, toolId: trace.toolId }, traceId);

    setIsSaving(true);
    setSaveError(null);

    try {
      const result = await new Promise<{ ok: boolean; message?: string }>((resolve) => {
        let settled = false;

        const handler = new JobRequestHandler<TraceResponse>({
          initFn: () => traceabilityApiClient.tracewrite({ trace: builtTrace }),
          statusFn: (jobId) => traceabilityApiClient.traceStatus({ jobId }),
          setResult: (res) => {
            if (settled) return;
            settled = true;
            resolve(
              res?.ok
                ? { ok: true }
                : { ok: false, message: (res as { message?: string } | null)?.message }
            );
          },
        });

        handler.start().catch((err: unknown) => {
          if (settled) return;
          settled = true;
          resolve({ ok: false, message: (err as Error)?.message || undefined });
        });
      });

      if (result.ok) {
        setIsDirty(false);
        return true;
      } else {
        setSaveError(result.message || t('toolView.advanced.editMode.saveError'));
        return false;
      }
    } catch (err: unknown) {
      setSaveError((err as Error)?.message || t('toolView.advanced.editMode.saveError'));
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [traceabilityApiClient, trace, editValues, t]);

  return { editValues, isDirty, isSaving, saveError, setField, reset, save };
}
